/**
 * Password validation utility for admin accounts
 * Requirements:
 * - At least 12 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one number
 * - At least one special character
 */

export interface PasswordValidationResult {
    isValid: boolean;
    requirements: {
        minLength: boolean;
        hasUppercase: boolean;
        hasLowercase: boolean;
        hasNumber: boolean;
        hasSpecial: boolean;
    };
}

/**
 * Validates a password against complexity requirements
 * @param password - The password to validate
 * @returns PasswordValidationResult with detailed requirement status
 */
export const validatePassword = (password: string): PasswordValidationResult => {
    const requirements = {
        minLength: password.length >= 12,
        hasUppercase: /[A-Z]/.test(password),
        hasLowercase: /[a-z]/.test(password),
        hasNumber: /\d/.test(password),
        hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password),
    };

    const isValid = Object.values(requirements).every(Boolean);

    return { isValid, requirements };
};

/**
 * Simple boolean check for password validity
 * @param password - The password to validate
 * @returns true if password meets all requirements
 */
export const isValidPassword = (password: string): boolean => {
    return validatePassword(password).isValid;
};
